import type {
  NavigationToModalInQueuePayload,
  NavigationToModalPayload
} from '@bcf-vanilla-ts-v1-logic-worker-modals/bloc/modal-outlet/types';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import type { Observable } from 'rxjs';
import { ReplaySubject, Subject } from 'rxjs';
import { provideModalQueueNavigatorService, type ModalQueueNavigatorService } from './modal-queue-navigator.service';
import type { ModalKindStandalone, ModalKindStandard } from './types';

export class ModalNavigatorService {
  private _navigateToModal$: ReplaySubject<NavigationToModalPayload> = new ReplaySubject<NavigationToModalPayload>(1);
  private _navigateToStandaloneModal$: ReplaySubject<ModalKindStandalone> = new ReplaySubject<ModalKindStandalone>(1);
  private _navigateToModalInQueue$: Subject<NavigationToModalInQueuePayload> =
    new Subject<NavigationToModalInQueuePayload>();
  private _navigateBackFromStandaloneModal$: Subject<ModalKindStandalone> = new Subject<ModalKindStandalone>();

  constructor(private _modalQueueNavigator: ModalQueueNavigatorService) {}

  public getNavigateToModal(): Observable<NavigationToModalPayload> {
    return this._navigateToModal$;
  }

  public getNavigateToStandaloneModal(): Observable<ModalKindStandalone> {
    return this._navigateToStandaloneModal$;
  }

  public getNavigateToModalInQueue(): Observable<NavigationToModalInQueuePayload> {
    return this._navigateToModalInQueue$;
  }

  public getNavigateBackFromStandaloneModal(): Observable<ModalKindStandalone> {
    return this._navigateBackFromStandaloneModal$;
  }

  public navigate(modalFragmentLink: ModalKindStandard, modalFragmentParam?: string, routerPath?: string): void;
  public navigate(modalFragmentLink: ModalKindStandard, modalFragmentParam?: string[], routerPath?: string): void;
  public navigate(
    modalFragmentLink: ModalKindStandard,
    modalFragmentParam?: string | string[],
    routerPath?: string
  ): void {
    const fixedModalParam: string | undefined = this._getModalFragmentParam(modalFragmentParam);
    this._navigateToModal$.next({
      modalFragmentLink: modalFragmentLink,
      modalFragmentParam: fixedModalParam,
      routerPath: routerPath
    });
  }

  private _getModalFragmentParam(modalFragmentParam: string | string[] | undefined): string | undefined {
    if (!modalFragmentParam) {
      return undefined;
    }
    if (Array.isArray(modalFragmentParam)) {
      return modalFragmentParam.join('~');
    }
    return modalFragmentParam.toString();
  }

  public closeModal(): void {
    this._modalQueueNavigator.removeFirstModalFromQueue(undefined);
  }

  public navigateToStandalone(modalKind: ModalKindStandalone): void {
    this._navigateToStandaloneModal$.next(modalKind);
  }

  public navigateBackFromStandalone(modalKind: ModalKindStandalone): void {
    this._navigateBackFromStandaloneModal$.next(modalKind);
  }

  public navigateInQueue(modalFragmentLink: ModalKindStandard, modalFragmentParam?: string): void {
    this._navigateToModalInQueue$.next({
      modalFragmentLink: modalFragmentLink,
      modalFragmentParam: modalFragmentParam
    });
  }
}

export function provideModalNavigatorService(): ModalNavigatorService {
  return provideSingleton(ModalNavigatorService, () => new ModalNavigatorService(provideModalQueueNavigatorService()));
}
